import React from 'react';
import propTypes from 'prop-types';
import { Table } from 'antd';
import { ErrorHandler, Title } from '@common/components';
import { gql, useQuery } from '@common/hooks';
import { Colors } from '@common/tokens';
import { ExpiredComplianceItemsCount } from 'works/legacy/components/expired-compliance-items-count';

const PENDING_APPROVAL_CHAINS_QUERY = gql`
  query ApprovalChainsPendingActionListQuery {
    approvalChainsPending {
      id
      approvable {
        ... on StaffingRequest {
          id
          __typename
        }
        ... on ExtensionOffer {
          id
          staffingRequestAssignment {
            id
            startDate
            endDate
            finalEndDate
            state
            billingRegularRate
            cancelledReason
            cancelledDetail
            extensionCount
            shiftsPerWeek
            hoursPerShift
            shiftType
            neverStarted
            organizationUnit {
              id
              name
              costCenter
              clinicalUnit {
                id
                displayName
              }
              organizationBusiness {
                id
                name
                city
                stateCode
              }
            }
            hiringLeader {
              id
              name
            }
            organization {
              id
              name
            }
            supplier {
              id
              name
            }
            worksProfile {
              id
              firstName
              lastName
              fullName
              profilePic
            }
            staffingRequest {
              id
              shiftType
              hoursPerShift
              shiftsPerWeek
              billingRegularRate
              code
              name
              externalId
              hiringLeader {
                id
                name
                firstName
                lastName
              }
              role {
                id
                name
                abbreviation
              }
            }
            worksList {
              id
              completedItemsCount
              totalItemsCount
            }
            ...ExpiredComplianceItemsCountFragment

            canOnboard {
              value
            }

            canOverrideClear {
              value
            }

            canViewClear {
              value
            }

            canWork {
              value
            }

            canComplete {
              value
            }

            canCancel {
              value
            }

            canUpdate {
              value
            }

            activeExtensionOffer {
              id
              state
            }
          }
          __typename
        }
      }
    }
  }
  ${ExpiredComplianceItemsCount.fragment}
`;

function getFlatColumns(columns) {
  return columns.map((col) => ({
    title: col.title,
    dataIndex: col.dataIndex,
    key: col.key,
    sorter: col.sorter,
    render: col.render,
  }));
}

export function ExtensionOffersApprovalsPendingList({ columns }) {
  const { loading, data, error } = useQuery(PENDING_APPROVAL_CHAINS_QUERY);
  const flatColumns = getFlatColumns(columns);

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="HeaderPendingApprovalItems"
        messageType="contextual"
      />
    );
  }

  if (
    loading ||
    !data?.approvalChainsPending ||
    !data.approvalChainsPending.length
  ) {
    return <></>;
  }

  const dataSource = data.approvalChainsPending
    .map((item) => item.approvable)
    .filter((ap) => ap.__typename === 'ExtensionOffer')
    .map((item) => item.staffingRequestAssignment);

  if (dataSource.length === 0) {
    return <></>;
  }

  return (
    <>
      <div style={{ marginTop: 32, marginBottom: 12 }}>
        <Title>Extension Offers Pending Your Approval</Title>
      </div>
      <Table
        rowKey="id"
        className="staffing-requests-pending-approval-list"
        columns={flatColumns}
        dataSource={dataSource}
        pagination={false}
      />
      <style jsx>{`
        :global(.staffing-requests-pending-approval-list) {
          margin-bottom: 40px;
        }
        :global(.staffing-requests-pending-approval-list tbody tr) {
          background-color: ${Colors.harvestGoldHyperLight};
          box-shadow: inset 4px 0px 0px ${Colors.harvestGold};
        }
      `}</style>
    </>
  );
}

ExtensionOffersApprovalsPendingList.propTypes = {
  columns: propTypes.arrayOf(
    propTypes.shape({
      title: propTypes.string,
      dataIndex: propTypes.string,
      key: propTypes.string,
      sorter: propTypes.bool,
      render: propTypes.func,
    })
  ),
};
